import React from 'react';

import ProductFirstPriorityDetails from '../../../../components/product-first-priority-details/product-first-priority-details';
import ProductListCardActions from '../product-list-card-actions/product-list-card-actions';
import ProductListCardDetails from '../product-list-card-details/product-list-card-details';
import ProductListReviewDetails from '../product-list-review-details/product-list-review-details';

import {
  useMediaQuery,
  useProductEntryDataLayerValues,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getNormalizedProductType } from '../../../../shared/utils/product/product';
import { setSelectedProductAvailabilityStore } from '../../utils/product-list-page-multistore-utils';

import {
  BundleType,
  ProductData,
  ProductTypeEnum
} from '../../../../shared/types/graphql-types';
import { ProductItem } from '../../../../shared/types/product';

import styles from './product-list-card.scss';

interface ProductListCardProps {
  isTreadwellTabSelected: boolean;
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product: ProductData;
}

function ProductListCard(props: ProductListCardProps) {
  const {
    isTreadwellTabSelected,
    onAddProductClick,
    onCheckInventoryClick,
    onViewProductClick,
    product
  } = props;

  const { isMinSm } = useMediaQuery();

  const normalizedProductType =
    getNormalizedProductType(product) || ProductTypeEnum.Tires;
  const { defaultQuantity, quantity, setQuantity, totalQuantity } =
    useProductQuantity({
      articleServiceType: product?.articleServiceType,
      bundleType: product?.rearProduct ? BundleType.StaggeredSet : undefined,
      productType: normalizedProductType
    });
  const productDetailUrl = product?.url || '';
  const hasTdsStoppingData =
    Boolean(product?.tdsData?.stopping?.dryRating) &&
    Boolean(product?.tdsData?.stopping?.wetRating);
  const hasTdsWinterData =
    Boolean(product?.tdsData?.winter?.snowHandlingRating) &&
    Boolean(product?.tdsData?.winter?.snowBrakeFeet);

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  const showFirstPriorityDetails = Boolean(
    product?.recommendationData || hasTdsStoppingData || hasTdsWinterData
  );

  if (window.isPreRendering) {
    return <div className={styles['container']} />;
  }

  return (
    <div
      className={styles['container']}
      data-testid="product-list-card"
      id={product && product.code ? `product-code-${product.code}` : ''}
    >
      <ProductListCardDetails
        hideAvailability={
          isMinSm && process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
        }
        onCheckInventoryClick={onCheckInventoryClick}
        onViewProductClick={onViewProductClick}
        product={product}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
        showPriceInDetails={
          !isMinSm && process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
        }
        totalQuantity={totalQuantity}
      />

      <ProductListCardActions
        defaultQuantity={defaultQuantity}
        hideProductPrice={
          !isMinSm && process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
        }
        isTreadwellTabSelected={isTreadwellTabSelected}
        onAddProductClick={onAddProductClick}
        onQuantityChange={setQuantity}
        onViewProductClick={onViewProductClick}
        product={product}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
        showAvailability={
          isMinSm && process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
        }
        showFirstPriorityDetails={showFirstPriorityDetails}
        showReportCard={
          process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
          isMinSm &&
          normalizedProductType === ProductTypeEnum.Tires
        }
        totalQuantity={totalQuantity}
      />

      {(process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true' || !isMinSm) &&
        normalizedProductType === ProductTypeEnum.Tires &&
        (showFirstPriorityDetails ? (
          <ProductFirstPriorityDetails
            dataReportUrl={productDetailUrl}
            isCollapsible={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            onClick={() => {
              trackAction(
                'product_view_pdl_data_click',
                getProductEntryDataLayerValues(product)
              );
              setSelectedProductAvailabilityStore(product);
            }}
            product={product}
          />
        ) : (
          <ProductListReviewDetails
            isTreadwellTabSelected={isTreadwellTabSelected}
            numberOfReviews={product?.reviewSummaryData?.totalCount || 0}
            onClickReadReviewsFn={() =>
              setSelectedProductAvailabilityStore(product)
            }
            product={product}
            productDetailUrl={productDetailUrl}
          />
        ))}
    </div>
  );
}

export default ProductListCard;
